import type { Membresia, Rol, Usuario } from "./api";

export type NombreIcono = "inicio" | "obra" | "plata" | "fiduciantes" | "ventas" | "configuracion" | "proyecto" | "mas" | "compartir" | "agregar" | "whatsapp" | "flecha" | "arrastrar" | "comprobante" | "falta-comprobante";

export interface Seccion {
  ruta: string;
  etiqueta: string;
  roles: readonly Rol[];
  /** false: la pantalla existe (rutas y roles salen de acá) pero no va en el menú; se llega desde otra pantalla. */
  enMenu?: false;
  /**
   * Ruta de la sección del menú que la contiene: no va suelta en el menú sino como vista de ésa, y la pantalla muestra
   * arriba un selector entre las dos (Mati, 2026-11-03: Presupuesto y Presupuestos recibidos son una sola cosa).
   */
  dentroDe?: string;
}

/** Una pantalla elegible dentro de una sección del menú (la sección misma va primera). */
export interface Vista {
  ruta: string;
  etiqueta: string;
}

/** Sección como la recibe el menú: con sus vistas si junta varias pantallas. */
export interface SeccionMenu extends Seccion {
  vistas?: readonly Vista[];
}

export interface Grupo {
  id: string;
  etiqueta: string;
  icono: NombreIcono;
  secciones: readonly SeccionMenu[];
  /** Va en el menú del nombre del usuario (header), no en la barra: así la barra queda en pocos grupos. */
  enMenuUsuario?: true;
}

const TODOS: readonly Rol[] = ["admin", "fiduciante", "contador", "escribano"];
/** Todos menos el fiduciante: él tiene su propio menú (GRUPOS_FIDUCIANTE). */
const SIN_FIDUCIANTE: readonly Rol[] = ["admin", "contador", "escribano"];
const CON_FIDUCIANTE: readonly Rol[] = ["admin", "contador", "fiduciante"];
const ADMIN_Y_CONTADOR: readonly Rol[] = ["admin", "contador"];
const SOLO_ADMIN: readonly Rol[] = ["admin"];

export const RUTA_PERFIL = "/perfil";

/**
 * Menú agrupado, en orden (Mati, 2026-10-20: pocos grupos plegables; Configuración va en el menú del nombre y "Armar
 * previsión" se abre desde Previsiones). Mati, 2026-11-03: Obra es construir y contratar, Plata la caja y los pagos
 * (cuentas corrientes incluidas), Ventas lo comercial; lo de los fiduciantes que se arma una vez (grupos, participación,
 * adjudicaciones, ganancia) va en Configuración → Fiduciantes; Terreno sale del menú. Las secciones con `dentroDe` son
 * vistas de otra (una sola entrada en el menú). Dentro de cada grupo, lo de todos los días primero. Es la única fuente de roles por pantalla: el menú y los guards de App.tsx (`rolesDe`)
 * leen de acá. Pantalla nueva → agregarla en su grupo con los roles que la ven.
 */
export const GRUPOS: readonly Grupo[] = [
  { id: "inicio", etiqueta: "Inicio", icono: "inicio", secciones: [{ ruta: "/", etiqueta: "Inicio", roles: TODOS }] },
  { id: "mi-cuenta", etiqueta: "Mi cuenta", icono: "fiduciantes", secciones: [{ ruta: "/mi-cuenta", etiqueta: "Mi cuenta", roles: ["fiduciante"] }] },
  {
    id: "obra",
    etiqueta: "Obra",
    icono: "obra",
    secciones: [
      { ruta: "/avance", etiqueta: "Avance", roles: TODOS },
      { ruta: "/cronograma", etiqueta: "Cronograma", roles: SOLO_ADMIN },
      { ruta: "/presupuesto", etiqueta: "Presupuesto", roles: ADMIN_Y_CONTADOR },
      { ruta: "/cotizaciones", etiqueta: "Recibidos", roles: ADMIN_Y_CONTADOR, dentroDe: "/presupuesto" },
      { ruta: "/proveedores", etiqueta: "Agenda", roles: ADMIN_Y_CONTADOR },
      // Se llega desde el Inicio (puesta en marcha), Avance ("Ver terreno") y la compra del terreno en Gastos.
      { ruta: "/terreno", etiqueta: "Terreno", roles: SIN_FIDUCIANTE, enMenu: false },
    ],
  },
  {
    id: "plata",
    etiqueta: "Plata",
    icono: "plata",
    secciones: [
      { ruta: "/caja", etiqueta: "Caja", roles: ADMIN_Y_CONTADOR },
      { ruta: "/gastos", etiqueta: "Gastos", roles: CON_FIDUCIANTE },
      { ruta: "/aportes", etiqueta: "Aportes", roles: ADMIN_Y_CONTADOR },
      { ruta: "/llamados", etiqueta: "Previsiones", roles: ADMIN_Y_CONTADOR, dentroDe: "/aportes" },
      { ruta: "/pedido", etiqueta: "Armar previsión", roles: SOLO_ADMIN, enMenu: false },
      { ruta: "/cuentas-corrientes", etiqueta: "Cuentas corrientes", roles: ADMIN_Y_CONTADOR },
      { ruta: "/cash-flow", etiqueta: "Cash flow", roles: SOLO_ADMIN },
      { ruta: "/reportes", etiqueta: "Reportes", roles: ADMIN_Y_CONTADOR },
      { ruta: "/honorarios", etiqueta: "Honorarios", roles: SOLO_ADMIN },
    ],
  },
  {
    id: "ventas",
    etiqueta: "Ventas",
    icono: "ventas",
    secciones: [
      { ruta: "/ventas", etiqueta: "Ventas", roles: SIN_FIDUCIANTE },
      { ruta: "/cobros", etiqueta: "Cobranzas", roles: SIN_FIDUCIANTE },
      { ruta: "/compradores", etiqueta: "Compradores", roles: SIN_FIDUCIANTE },
      { ruta: "/unidades", etiqueta: "Unidades", roles: SIN_FIDUCIANTE },
      { ruta: "/ponderacion", etiqueta: "Ponderación", roles: SOLO_ADMIN },
      { ruta: "/proyecto", etiqueta: "Proyecto", roles: ["admin", "fiduciante"] },
      { ruta: "/cac", etiqueta: "Índice CAC", roles: SIN_FIDUCIANTE },
    ],
  },
  {
    id: "configuracion",
    etiqueta: "Configuración",
    icono: "configuracion",
    enMenuUsuario: true,
    secciones: [
      { ruta: "/configuracion/fideicomiso", etiqueta: "Fideicomiso", roles: SOLO_ADMIN },
      { ruta: "/fiduciantes", etiqueta: "Fiduciantes", roles: ADMIN_Y_CONTADOR },
      { ruta: "/grupos", etiqueta: "Grupos", roles: ADMIN_Y_CONTADOR, dentroDe: "/fiduciantes" },
      { ruta: "/participacion", etiqueta: "Participación", roles: ADMIN_Y_CONTADOR, dentroDe: "/fiduciantes" },
      { ruta: "/adjudicaciones", etiqueta: "Adjudicaciones", roles: ADMIN_Y_CONTADOR, dentroDe: "/fiduciantes" },
      { ruta: "/ganancia", etiqueta: "Ganancia", roles: ADMIN_Y_CONTADOR, dentroDe: "/fiduciantes" },
      { ruta: "/configuracion/usuarios", etiqueta: "Usuarios", roles: SOLO_ADMIN },
      { ruta: "/configuracion/rubros", etiqueta: "Rubros", roles: SOLO_ADMIN },
      { ruta: "/configuracion/ponderacion", etiqueta: "Ponderación", roles: SOLO_ADMIN },
      { ruta: "/configuracion/avance", etiqueta: "Avance de obra", roles: SOLO_ADMIN },
      { ruta: "/configuracion/mensajes", etiqueta: "Mensajes a proveedores", roles: SOLO_ADMIN },
      { ruta: "/configuracion/avisos", etiqueta: "Avisos", roles: SOLO_ADMIN },
      { ruta: "/historial", etiqueta: "Historial", roles: ADMIN_Y_CONTADOR },
    ],
  },
];

/** Si `pathname` es esa ruta o una ficha suya (`/gastos/12` está en `/gastos`); "/" sólo es el Inicio. */
export function enRuta(ruta: string, pathname: string): boolean {
  return ruta === "/" ? pathname === "/" : pathname === ruta || pathname.startsWith(`${ruta}/`);
}

/** La vista de la sección en la que está `pathname`, si la sección junta varias pantallas. */
export function vistaActiva(s: { vistas?: readonly Vista[] }, pathname: string): Vista | undefined {
  return s.vistas?.find((v) => enRuta(v.ruta, pathname));
}

/** Si `pathname` está en esa sección del menú: su ruta o la de alguna de sus vistas. */
export function seccionCoincide(s: { ruta: string; vistas?: readonly Vista[] }, pathname: string): boolean {
  return enRuta(s.ruta, pathname) || vistaActiva(s, pathname) !== undefined;
}

/** Las secciones que ve el rol, con las de `dentroDe` metidas como vistas de su sección (que va primera). */
function seccionesDelMenu(secciones: readonly Seccion[], rol: Rol): SeccionMenu[] {
  const visibles = secciones.filter((s) => s.roles.includes(rol) && s.enMenu !== false);
  return visibles
    .filter((s) => !s.dentroDe)
    .map((s) => {
      const hijas = visibles.filter((h) => h.dentroDe === s.ruta);
      return hijas.length === 0 ? s : { ...s, vistas: [s, ...hijas].map(({ ruta, etiqueta }) => ({ ruta, etiqueta })) };
    });
}

const filtrarPorRol = (grupos: readonly Grupo[], rol: Rol): Grupo[] =>
  grupos.map((g) => ({ ...g, secciones: seccionesDelMenu(g.secciones, rol) })).filter((g) => g.secciones.length > 0);

/** El menú de siempre de ese rol; el fiduciante tiene el suyo (GRUPOS_FIDUCIANTE). */
export function gruposVisibles(rol: Rol): Grupo[] {
  return filtrarPorRol(rol === "fiduciante" ? GRUPOS_FIDUCIANTE : GRUPOS, rol);
}

type SeccionSinRoles = Omit<Seccion, "roles">;
type GrupoSinRoles = Omit<Grupo, "secciones"> & { secciones: readonly SeccionSinRoles[] };

/** Arma el grupo con los roles de cada ruta tal como están en GRUPOS (`rolesDe` tira si la ruta no existe). */
const conRoles = (g: GrupoSinRoles): Grupo => ({ ...g, secciones: g.secciones.map((s) => ({ ...s, roles: rolesDe(s.ruta) })) });

/**
 * Menú del fiduciante (Mati, 2026-10-20): una pestaña por pantalla, sin grupos. Inicio es el tablero que junta un poco
 * de cada una. Sólo cambia el menú: los roles salen de GRUPOS.
 */
export const GRUPOS_FIDUCIANTE: readonly Grupo[] = (
  [
    { id: "inicio", etiqueta: "Inicio", icono: "inicio", secciones: [{ ruta: "/", etiqueta: "Inicio" }] },
    { id: "avance", etiqueta: "Avance", icono: "obra", secciones: [{ ruta: "/avance", etiqueta: "Avance" }] },
    { id: "gastos", etiqueta: "Gastos", icono: "plata", secciones: [{ ruta: "/gastos", etiqueta: "Gastos" }] },
    { id: "proyecto", etiqueta: "Proyecto", icono: "proyecto", secciones: [{ ruta: "/proyecto", etiqueta: "Proyecto" }] },
    { id: "mi-cuenta", etiqueta: "Mi cuenta", icono: "fiduciantes", secciones: [{ ruta: "/mi-cuenta", etiqueta: "Mi cuenta" }] },
  ] satisfies GrupoSinRoles[]
).map(conRoles);

/** Reparte los grupos entre la barra (lateral o inferior) y el menú del nombre del usuario. */
export function separarMenu(grupos: readonly Grupo[]): { barra: Grupo[]; usuario: Grupo[] } {
  return { barra: grupos.filter((g) => !g.enMenuUsuario), usuario: grupos.filter((g) => g.enMenuUsuario) };
}

export function seccionesVisibles(rol: Rol): Seccion[] {
  return gruposVisibles(rol).flatMap((g) => g.secciones);
}

/** Roles de la pantalla con esa ruta del menú (para ConRol). Falla fuerte si la ruta no está: así no hay guards desalineados. */
export function rolesDe(ruta: string): readonly Rol[] {
  const s = GRUPOS.flatMap((g) => g.secciones).find((x) => x.ruta === ruta);
  if (!s) throw new Error(`La ruta ${ruta} no está en GRUPOS`);
  return s.roles;
}

/** La membresía del desarrollo elegido (selector de la barra); si no hay elegido o ya no está, la de menor id. */
export function membresiaActual(usuario: Usuario, elegido: number | null = null): Membresia | null {
  const porId = elegido === null ? undefined : usuario.membresias.find((m) => m.fideicomiso_id === elegido);
  return porId ?? [...usuario.membresias].sort((a, b) => a.fideicomiso_id - b.fideicomiso_id)[0] ?? null;
}

const CLAVE_DESARROLLO = "desarrollo_actual";

/** Último desarrollo elegido en este navegador (null si no hay o el storage no está disponible). */
export function leerDesarrolloElegido(): number | null {
  try {
    const v = window.localStorage.getItem(CLAVE_DESARROLLO);
    return v && /^\d+$/.test(v) ? Number(v) : null;
  } catch {
    return null;
  }
}

export function guardarDesarrolloElegido(fid: number): void {
  try {
    window.localStorage.setItem(CLAVE_DESARROLLO, String(fid));
  } catch {
    // modo privado o storage bloqueado: se pierde la elección al recargar, nada más
  }
}
